import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositoryAPI } from '../../services/api';
import { repoKeys } from '../../query/queryKeys';
import toast from '../../services/toast';
import { isCodeIndexInProgress } from '../../utils/codeIndexProgress';

/**
 * Primary repository detail query with polling while sync or code index is active.
 */
export function useRepositoryDetailQuery(id) {
  const repoQuery = useQuery({
    queryKey: repoKeys.byId(id),
    queryFn: async () => {
      const response = await repositoryAPI.getById(id);
      return response.repository;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.isSyncing) return 12000;
      if (isCodeIndexInProgress(d)) return 8000;
      return false;
    },
  });

  const repository = repoQuery.data ?? null;
  const loading = repoQuery.isPending;
  const codeIndexActive = isCodeIndexInProgress(repository);
  const codeIndexProgressPct =
    typeof repository?.codeIndexProgress === 'number' &&
    Number.isFinite(repository.codeIndexProgress)
      ? Math.max(0, Math.min(100, repository.codeIndexProgress))
      : 0;

  useEffect(() => {
    if (repoQuery.isError && repoQuery.error) {
      toast.error(repoQuery.error.message || 'Failed to fetch repository');
    }
  }, [repoQuery.isError, repoQuery.error]);

  return {
    repository,
    loading,
    repoQuery,
    codeIndexActive,
    codeIndexProgressPct,
  };
}
